import type { CategoryAttribute, Item, ItemAttribute, ItemCategory } from '../api/masters';
import { shortCodeProblem } from '../units/garmentCode';

/**
 * The rules behind adding a design and a category — the website's design and category pages, and
 * the phone's New design and New category (`docs/Mobile_Redesign_Plan.md`, step 10), which ask
 * for less and send the same requests.
 */

/* ------------------------------------------------------------------ choices (moved from the web) */

/**
 * Where a category may sit: every category except itself and anything beneath it.
 *
 * The server refuses both — a category cannot be its own parent, nor sit under one of its own
 * descendants — and offering them anyway would mean the only way to learn the rule is to hit it.
 *
 * Descendants are found by depth rather than by re-deriving the tree: the list arrives in tree-walk
 * order, so everything after this row with a greater depth is beneath it, and the run ends at the
 * first row back at its own level or shallower.
 */
export function parentChoices(
  categories: readonly ItemCategory[],
  editing: ItemCategory | undefined,
): { label: string; value: number }[] {
  const start = editing ? categories.findIndex((c) => c.id === editing.id) : -1;

  const end =
    start === -1
      ? -1
      : categories.findIndex((c, i) => i > start && c.depth <= (editing?.depth ?? 0));

  return categories
    .filter((_, index) => {
      if (start === -1) {
        return true;
      }

      // Itself, and the run of deeper rows after it.
      return index < start || (end !== -1 && index >= end);
    })
    .map((c) => ({ label: `${'  '.repeat(c.depth)}${c.name}`, value: c.id }));
}

/**
 * The categories a design may be filed under. A shared design may only sit in a shared category —
 * the server refuses otherwise — so ticking "shared" narrows the list at once rather than failing on
 * save. Scope is fixed once created, so an existing design is offered everything.
 */
export function categoryChoices(
  categories: readonly { id: number; name: string; depth: number; isShared: boolean }[],
  shared: boolean,
  isEdit: boolean,
): { label: string; value: number }[] {
  return categories
    .filter((category) => isEdit || !shared || category.isShared)
    .map((category) => ({
      label: `${'  '.repeat(category.depth)}${category.name}`,
      value: category.id,
    }));
}

/**
 * The request body. No screen offers the old rental rate any more — nothing charges it, a design's
 * price is its rate card — but an edit states the whole record, so whatever is stored is sent back
 * untouched rather than silently cleared. The column goes in the migration that drops it.
 */
export function designRequest<T extends object>(
  values: T,
  existing: { baseRentalRate: number | null } | undefined,
): T & { baseRentalRate: number | null } {
  return { ...values, baseRentalRate: existing?.baseRentalRate ?? null };
}

/* ------------------------------------------------------------------ typed numbers */

/** A typed number: empty is "not given" (null), anything unreadable is NaN for the checks to name. */
export function typedNumber(text: string): number | null {
  const cleaned = text.replace(/[,\s₹]/g, '');

  return cleaned === '' ? null : Number(cleaned);
}

const isBad = (value: number | null) => value !== null && (Number.isNaN(value) || value < 0);

/* ------------------------------------------------------------------ a design */

/** What the phone's design screen holds — numbers as typed, so a half-typed figure is not lost. */
export interface DesignForm {
  name: string;
  code: string;
  categoryId: number | null;
  replacementValue: string;
  brand: string;
  fabric: string;
  description: string;
  /** Empty inherits the category's. */
  bufferDays: string;
  hsnCode: string;
  taxRatePct: string;
  /** The rate when sold. The phone does not show it; it is carried so a save does not clear it. */
  saleTaxRatePct: string;
  /** The price when sold, GST included — carried the same way. */
  salePrice: string;
}

export function emptyDesignForm(categoryId: number | null = null): DesignForm {
  return {
    name: '',
    code: '',
    categoryId,
    replacementValue: '',
    brand: '',
    fabric: '',
    description: '',
    bufferDays: '',
    hsnCode: '',
    taxRatePct: '',
    saleTaxRatePct: '',
    salePrice: '',
  };
}

/** A stored design as the form shows it — its own values, never the ones it inherits. */
export function designFormOf(item: Item): DesignForm {
  const text = (value: number | null) => (value === null ? '' : String(value));

  return {
    name: item.name,
    code: item.code,
    categoryId: item.categoryId,
    replacementValue: text(item.replacementValue),
    brand: item.brand ?? '',
    fabric: item.fabric ?? '',
    description: item.description ?? '',
    bufferDays: text(item.turnaroundBufferDays),
    hsnCode: item.hsnCode ?? '',
    taxRatePct: text(item.taxRatePct),
    saleTaxRatePct: text(item.saleTaxRatePct),
    salePrice: text(item.salePrice),
  };
}

/**
 * A new design started like another (canvas "Master entry flow", direction C, owner 2026-10-27):
 * its category, replacement value, brand, fabric and its own days to clean and tax — what a run of
 * designs from one supplier shares. **Never its name, short code or photographs**, which make it a
 * different design; nor its description or sale price, which belong to that one piece. Only what
 * the other design sets itself is copied, so what it inherits keeps following the category.
 */
export function designFormLike(item: Item): DesignForm {
  const own = designFormOf(item);

  return {
    ...emptyDesignForm(item.categoryId),
    replacementValue: own.replacementValue,
    brand: own.brand,
    fabric: own.fabric,
    bufferDays: own.bufferDays,
    hsnCode: own.hsnCode,
    taxRatePct: own.taxRatePct,
    saleTaxRatePct: own.saleTaxRatePct,
  };
}

/**
 * What stops the design being saved. The short code's only refusal is the server's — empty or a
 * hyphen; anything else about it is advice (`shortCodeProblem`), shown and not enforced.
 */
export function designProblems(form: DesignForm, isEdit: boolean): string[] {
  const problems: string[] = [];

  if (!form.name.trim()) {
    problems.push('Give the design a name.');
  }

  if (form.categoryId === null) {
    problems.push('Choose its category.');
  }

  if (!isEdit) {
    const code = form.code.trim();

    if (!code) {
      problems.push('Give it a short code — it starts every garment code.');
    } else if (code.includes('-')) {
      problems.push(
        'No hyphens in the short code: a hyphen separates the parts of a garment code.',
      );
    }
  }

  if (isBad(typedNumber(form.replacementValue))) {
    problems.push('The replacement value must be a figure, nothing below zero.');
  }

  const buffer = typedNumber(form.bufferDays);

  if (isBad(buffer) || (buffer !== null && !Number.isInteger(buffer))) {
    problems.push('Days to clean must be a whole number, nothing below zero.');
  }

  const tax = typedNumber(form.taxRatePct);

  if (isBad(tax) || (tax !== null && tax > 100)) {
    problems.push('The tax rate must be between 0 and 100.');
  }

  const sale = typedNumber(form.saleTaxRatePct);

  if (isBad(sale) || (sale !== null && sale > 100)) {
    problems.push('The sale tax rate must be between 0 and 100.');
  }

  if (isBad(typedNumber(form.salePrice))) {
    problems.push('The sale price must be a figure, nothing below zero.');
  }

  return problems;
}

/** The advice under the short code — "Two to five letters work best" — or null when it is good. */
export function shortCodeAdvice(code: string): string | null {
  return code.trim() ? shortCodeProblem(code) : null;
}

const orNull = (text: string) => text.trim() || null;

function designFields(form: DesignForm) {
  return {
    name: form.name.trim(),
    categoryId: form.categoryId ?? 0,
    description: orNull(form.description),
    brand: orNull(form.brand),
    fabric: orNull(form.fabric),
    turnaroundBufferDays: typedNumber(form.bufferDays),
    hsnCode: orNull(form.hsnCode),
    taxRatePct: typedNumber(form.taxRatePct),
    replacementValue: typedNumber(form.replacementValue),
  };
}

/**
 * The sale rate as each verb takes it. A create sends the figure; an update sends it wrapped, because
 * an update with no `saleTaxRate` at all leaves the stored rate alone — which is what a phone built
 * before the field existed sends — and a bare `null` could not be told apart from that.
 */
export function saleRateField(pct: number | null, isEdit: boolean) {
  return isEdit ? { saleTaxRate: { pct } } : { saleTaxRatePct: pct };
}

/** The sale price as each verb takes it — bare on a create, wrapped on an update, as the rate is. */
export function salePriceField(amount: number | null, isEdit: boolean) {
  return isEdit ? { salePrice: { amount } } : { salePrice: amount };
}

/** The category's half of `saleRateField`, under its own names. */
export function defaultSaleRateField(pct: number | null, isEdit: boolean) {
  return isEdit ? { defaultSaleTaxRate: { pct } } : { defaultSaleTaxRatePct: pct };
}

/** `POST /api/item` — a design at the acting branch. */
export function createDesignRequest(form: DesignForm) {
  return designRequest(
    {
      code: form.code.trim().toUpperCase(),
      ...designFields(form),
      ...saleRateField(typedNumber(form.saleTaxRatePct), false),
      ...salePriceField(typedNumber(form.salePrice), false),
    },
    undefined,
  );
}

/**
 * `PUT /api/item/{id}` — the whole record, so what the phone does not show (the hosted link, the
 * service code, active, the old rate) goes back as it was rather than being cleared.
 */
export function updateDesignRequest(form: DesignForm, existing: Item) {
  return designRequest(
    {
      ...designFields(form),
      primaryImageUrl: existing.primaryImageUrl,
      sacCode: existing.sacCode,
      isActive: existing.isActive,
      ...saleRateField(typedNumber(form.saleTaxRatePct), true),
      ...salePriceField(typedNumber(form.salePrice), true),
    },
    existing,
  );
}

/* ------------------------------------------------------------------ a category */

export interface CategoryForm {
  name: string;
  /** Fixed once created; suggested from the name until typed. */
  code: string;
  parentId: number | null;
  /** Null inherits from the category above (none at the top means none). */
  bufferDays: number | null;
  isAccessory: boolean;
  /** Every branch, rather than only the acting one. */
  shared: boolean;
  hsnCode: string;
  sacCode: string;
  taxRatePct: string;
  /** Attributes to assign here as variant-forming — what tells its garments apart. */
  axes: number[];
}

export function emptyCategoryForm(): CategoryForm {
  return {
    name: '',
    code: '',
    parentId: null,
    bufferDays: null,
    isAccessory: false,
    shared: false,
    hsnCode: '',
    sacCode: '',
    taxRatePct: '',
    axes: [],
  };
}

/**
 * A new category started like another (direction C): it sits beside it — the same parent, so the
 * same inherited axes — and takes its own days to clean, accessory and branch scope, tax codes and
 * what tells its own garments apart, in their order. Never its name, code or photograph. An axis it
 * only inherits is left to the parent, as it is for the other one.
 */
export function categoryFormLike(
  category: ItemCategory,
  attributes: readonly Pick<
    CategoryAttribute,
    'attributeId' | 'isVariantForming' | 'isInherited' | 'sortOrder'
  >[],
): CategoryForm {
  return {
    ...emptyCategoryForm(),
    parentId: category.parentId,
    bufferDays: category.turnaroundBufferDays,
    isAccessory: category.isAccessory,
    shared: category.isShared,
    hsnCode: category.defaultHsnCode ?? '',
    sacCode: category.defaultSacCode ?? '',
    taxRatePct: category.defaultTaxRatePct === null ? '' : String(category.defaultTaxRatePct),
    axes: attributes
      .filter((axis) => axis.isVariantForming && !axis.isInherited)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((axis) => axis.attributeId),
  };
}

/** "Kurta Pyjama" is KURTA_PYJAMA — the server's own normalising, applied before it is shown. */
export function categoryCodeFrom(name: string): string {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** At most three axes per category, counting the ones it inherits — the server's own cap. */
export const maxAxes = 3;

/**
 * The attributes that could tell this category's garments apart: list attributes that are active
 * and not already inherited from above. Only a list can be an axis — a number or text has no values
 * to choose between at intake.
 */
export function axisCandidates(
  attributes: readonly ItemAttribute[],
  inherited: readonly Pick<CategoryAttribute, 'attributeId' | 'isVariantForming'>[],
): ItemAttribute[] {
  const taken = new Set(
    inherited.filter((axis) => axis.isVariantForming).map((axis) => axis.attributeId),
  );

  return attributes.filter(
    (attribute) => attribute.dataType === 'List' && attribute.isActive && !taken.has(attribute.id),
  );
}

export function categoryProblems(form: CategoryForm, inheritedAxes: number): string[] {
  const problems: string[] = [];

  if (!form.name.trim()) {
    problems.push('Give the category a name.');
  }

  if (!categoryCodeFrom(form.code || form.name)) {
    problems.push('Give it a code of letters or digits.');
  }

  if (form.bufferDays !== null && (form.bufferDays < 0 || !Number.isInteger(form.bufferDays))) {
    problems.push('Days to clean must be a whole number, nothing below zero.');
  }

  const tax = typedNumber(form.taxRatePct);

  if (isBad(tax) || (tax !== null && tax > 100)) {
    problems.push('The tax rate must be between 0 and 100.');
  }

  if (inheritedAxes + form.axes.length > maxAxes) {
    problems.push(
      `At most ${maxAxes} things can tell a category's garments apart, counting the ones it takes from above.`,
    );
  }

  return problems;
}

/** `POST /api/item-category`. The axes are assigned afterwards, one call each. */
export function createCategoryRequest(form: CategoryForm) {
  return {
    code: categoryCodeFrom(form.code || form.name),
    name: form.name.trim(),
    shared: form.shared,
    parentId: form.parentId,
    turnaroundBufferDays: form.bufferDays,
    isAccessory: form.isAccessory,
    defaultHsnCode: orNull(form.hsnCode),
    defaultSacCode: orNull(form.sacCode),
    defaultTaxRatePct: typedNumber(form.taxRatePct),
  };
}

/** `POST /api/item-category/{id}/attributes` for one chosen axis. */
export function axisAssignment(attributeId: number, index: number) {
  return { attributeId, isRequired: true, isVariantForming: true, sortOrder: index + 1 };
}
